import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { updateQuantity, removeFromCart } from '../features/cart/cartSlice';
import { apiFetch } from '../utils/api';
import axios from '../axiosConfig';
import {
  formatPhoneNumber,
  openWhatsAppOrder,
  getVirtualAccountFromStore,
  generateCartCheckoutUrl,
  generateWhatsAppOrderUrl
} from '../utils/whatsappOrder';
import { getPlatformDomainName } from '../config/platformConfig';
import DeliveryMethodSelector from './checkout/DeliveryMethodSelector';
import CustomerFacingSubtotal from './checkout/CustomerFacingSubtotal';
import { getQuoteTaxInfo } from '../utils/merchantTax';

// Note: no all-Nigerian-states fallback. If the merchant hasn't
// configured any shipping zones we render a "delivery not set up"
// notice instead of letting the customer pick a state the merchant
// can't service. Same policy as the main CheckoutModal.

const currency = new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 });

const WhatsAppOrderModal = ({
  isOpen,
  onClose,
  items,
  product,
  productQuantity = 1,
  selectedVariations = {},
  storeSettings,
  storeSlug
}) => {
  const dispatch = useDispatch();

  // Determine if we're in single product mode (from ProductPage) vs cart mode
  const isSingleProductMode = !!product && !items;

  // Local cart state for UI updates
  const [localItems, setLocalItems] = useState([]);

  // Customer details
  const [customerDetails, setCustomerDetails] = useState({
    fullName: '',
    email: '',
    whatsappNumber: ''
  });
  const [customerExpanded, setCustomerExpanded] = useState(true);

  // Delivery details
  const [deliveryDetails, setDeliveryDetails] = useState({
    address: '',
    city: '',
    state: '',
    additionalInfo: ''
  });
  const [deliveryExpanded, setDeliveryExpanded] = useState(false);

  // Coupon
  const [couponCode, setCouponCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');

  // Calculations
  const [shippingInfo, setShippingInfo] = useState(null);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [serviceChargeInfo, setServiceChargeInfo] = useState(null);
  const [calculationsLoading, setCalculationsLoading] = useState(false);

  // Merchant delivery states (fetched from shipping zones config)
  const [merchantStates, setMerchantStates] = useState([]);

  // Chowdeck delivery method state
  const [deliveryMethod, setDeliveryMethod] = useState('STATIC');
  const [chowdeckFeeId, setChowdeckFeeId] = useState(null);
  const [chowdeckDeliveryFee, setChowdeckDeliveryFee] = useState(null);

  // Fetch merchant delivery states when modal opens
  useEffect(() => {
    if (!isOpen || !storeSlug) return;

    const fetchMerchantStates = async () => {
      try {
        const response = await apiFetch(`/shipping-zones/merchant-states/${storeSlug}`);
        if (response.ok) {
          const data = await response.json();
          if (data?.states && data.states.length > 0) {
            setMerchantStates(data.states);
          }
        }
      } catch (error) {
        console.error('Failed to fetch merchant delivery states:', error);
      }
    };

    fetchMerchantStates();
  }, [isOpen, storeSlug]);

  // Initialize local items when modal opens
  useEffect(() => {
    if (isOpen) {
      if (isSingleProductMode && product) {
        // Single product mode: create a cart-like item from the product
        const variationText = Object.entries(selectedVariations)
          .map(([key, value]) => `${key}: ${value}`)
          .join(', ');

        // product.price is already the correct variation price (passed from ProductPage's currentDisplayPrice)
        const itemPrice = parseFloat(product.price);

        setLocalItems([{
          product: {
            ...product,
            variationText: variationText || null
          },
          price: itemPrice, // Set the correct variation price
          qty: productQuantity
        }]);
      } else if (items) {
        // Cart mode: use items array
        setLocalItems(items.map(item => ({ ...item })));
      }
    }
  }, [isOpen, items, product, productQuantity, selectedVariations, isSingleProductMode]);

  // Calculate subtotal from local items
  // Use item.price (variation price) if available, otherwise fall back to item.product.price (base price)
  const subtotal = localItems.reduce((sum, item) => {
    const itemPrice = item.price ?? item.product.price;
    return sum + (itemPrice * item.qty);
  }, 0);

  // Calculate shipping when state changes
  useEffect(() => {
    const calculateShipping = async () => {
      if (!deliveryDetails.state || !storeSlug) return;

      setShippingLoading(true);
      try {
        const response = await apiFetch('/shipping-zones/calculate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            state: deliveryDetails.state,
            city: deliveryDetails.city,
            address: deliveryDetails.address,
            orderSubtotal: subtotal,
            storeSlug: storeSlug
          })
        });

        if (response.ok) {
          const data = await response.json();
          setShippingInfo(data);
        }
      } catch (error) {
        console.error('Failed to calculate shipping:', error);
      } finally {
        setShippingLoading(false);
      }
    };

    calculateShipping();
  }, [deliveryDetails.state, subtotal, storeSlug]);

  // Calculate service charge and tax
  const calculateTotals = useCallback(async () => {
    if (!storeSlug || subtotal <= 0) return;

    setCalculationsLoading(true);
    try {
      const shippingCost = shippingInfo?.shippingCost || 0;
      const discountAmount = appliedDiscount?.discountAmount || 0;

      const response = await apiFetch('/service-charge/calculate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subtotal: subtotal,
          shippingFee: shippingCost,
          discountAmount: discountAmount,
          storeSlug: storeSlug
        })
      });

      if (response.ok) {
        const data = await response.json();
        setServiceChargeInfo(data);
      }
    } catch (error) {
      console.error('Failed to calculate service charge:', error);
    } finally {
      setCalculationsLoading(false);
    }
  }, [storeSlug, subtotal, shippingInfo?.shippingCost, appliedDiscount?.discountAmount]);

  useEffect(() => {
    calculateTotals();
  }, [calculateTotals]);

  // Handle delivery method change (Chowdeck integration)
  const handleDeliveryMethodChange = useCallback((data) => {
    console.log('[WhatsAppOrderModal] Delivery method changed:', data);
    setDeliveryMethod(data.method);

    if (data.method === 'CHOWDECK') {
      setChowdeckFeeId(data.feeId);
      setChowdeckDeliveryFee(data.fee);
    } else {
      setChowdeckFeeId(null);
      setChowdeckDeliveryFee(null);
    }
  }, []);

  // Handle quantity change
  const handleQuantityChange = (productId, newQty) => {
    if (newQty < 1) return;

    setLocalItems(prev => prev.map(item =>
      (item.product._id === productId || item.product.id === productId)
        ? { ...item, qty: newQty }
        : item
    ));

    // Only update Redux store in cart mode (not single product mode)
    if (!isSingleProductMode) {
      dispatch(updateQuantity({ productId, quantity: newQty }));
    }
  };

  // Handle remove item
  const handleRemoveItem = (productId) => {
    // In single product mode, just close the modal since there's only one product
    if (isSingleProductMode) {
      onClose();
      return;
    }

    setLocalItems(prev => prev.filter(item =>
      item.product._id !== productId && item.product.id !== productId
    ));

    // Also update Redux store
    dispatch(removeFromCart({ productId }));
  };

  // Apply coupon
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    setCouponLoading(true);
    setCouponError('');

    try {
      const merchantId = storeSettings?.ownerId || storeSettings?.store?.ownerId;

      const response = await apiFetch('/discounts/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: couponCode.trim(),
          merchantId: merchantId,
          orderData: {
            subtotal: subtotal,
            shippingFee: shippingInfo?.shippingCost || 0,
            items: localItems.map(item => ({
              productId: item.product._id || item.product.id,
              quantity: item.qty,
              price: item.product.price
            }))
          },
          storeSlug: storeSlug
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setAppliedDiscount({
          code: couponCode.trim(),
          discountAmount: data.discountAmount,
          details: data.discount
        });
        setCouponError('');
      } else {
        setCouponError(data.error || 'Invalid coupon code');
      }
    } catch (error) {
      setCouponError('Failed to validate coupon');
    } finally {
      setCouponLoading(false);
    }
  };

  // Remove coupon
  const handleRemoveCoupon = () => {
    setCouponCode('');
    setAppliedDiscount(null);
    setCouponError('');
  };

  // Calculate final totals
  const discountAmount = appliedDiscount?.discountAmount || 0;
  const quoteTax = getQuoteTaxInfo(serviceChargeInfo || {});
  const taxAmount = quoteTax.amount;
  const serviceCharge = serviceChargeInfo?.serviceCharge || 0;
  const serviceChargeWhoPays = (serviceChargeInfo?.whoPays || 'customer').toLowerCase();
  // Use Chowdeck fee when selected, otherwise use standard shipping
  const shippingCost = deliveryMethod === 'CHOWDECK' ? (chowdeckDeliveryFee || 0) : (shippingInfo?.shippingCost || 0);
  // Always calculate grand total on frontend to ensure shipping is included
  // Exclude service charge from customer total when merchant pays
  const customerServiceCharge = serviceChargeWhoPays === 'customer' ? serviceCharge : 0;
  const grandTotal = subtotal - discountAmount + taxAmount + customerServiceCharge + shippingCost;

  // Generate WhatsApp message
  const generateWhatsAppMessage = (orderUrl = null) => {
    const virtualAccount = getVirtualAccountFromStore(storeSettings);
    let message = '';

    // Header
    message += `Hi *${storeSettings?.name || storeSettings?.storeName || 'Store'}*\n\n`;
    message += `I'd like to place an order:\n\n`;

    // Order Details
    message += `*ORDER DETAILS*\n\n`;

    localItems.forEach((item, index) => {
      const itemPrice = item.price ?? item.product.price;
      const itemTotal = itemPrice * item.qty;
      message += `${index + 1}. *${item.product.name}*\n`;
      if (item.product.variationText) {
        message += `   ${item.product.variationText}\n`;
      }
      message += `   ${item.qty} x ${currency.format(itemPrice)}\n`;
      message += `   = ${currency.format(itemTotal)}\n\n`;
    });

    // Subtotal
    if (serviceCharge > 0 && serviceChargeWhoPays === 'customer') {
      message += `*Subtotal incl. transaction fee:* ${currency.format(subtotal + serviceCharge)}\n`;
      message += `_(Items ${currency.format(subtotal)} + transaction fee ${currency.format(serviceCharge)})_\n`;
    } else {
      message += `*Subtotal:* ${currency.format(subtotal)}\n`;
    }

    // Discount
    if (discountAmount > 0) {
      message += `*Discount (${appliedDiscount?.code}):* -${currency.format(discountAmount)}\n`;
    }

    // Tax/VAT
    if (taxAmount > 0) {
      const taxName = quoteTax.name || 'VAT';
      const taxRate = quoteTax.rate || 0;
      message += `*${taxName} (${taxRate}%):* ${currency.format(taxAmount)}\n`;
    }

    // Shipping / Delivery Method
    if (shippingCost > 0) {
      if (deliveryMethod === 'CHOWDECK') {
        message += `*⚡ Chowdeck Express (${deliveryDetails.state}):* ${currency.format(shippingCost)}\n`;
      } else {
        message += `*Shipping (${deliveryDetails.state}):* ${currency.format(shippingCost)}\n`;
      }
    }

    // Grand Total
    message += `\n*GRAND TOTAL: ${currency.format(grandTotal)}*\n\n`;

    // Customer Details
    message += `*CUSTOMER DETAILS*\n`;
    message += `Name: ${customerDetails.fullName}\n`;
    if (customerDetails.email) {
      message += `Email: ${customerDetails.email}\n`;
    }
    message += `WhatsApp: ${customerDetails.whatsappNumber}\n\n`;

    // Delivery Details
    if (deliveryDetails.address || deliveryDetails.state) {
      message += `*DELIVERY DETAILS*\n`;
      if (deliveryDetails.address) {
        message += `Address: ${deliveryDetails.address}\n`;
      }
      if (deliveryDetails.city) {
        message += `City: ${deliveryDetails.city}\n`;
      }
      if (deliveryDetails.state) {
        message += `State: ${deliveryDetails.state}\n`;
      }
      if (deliveryDetails.additionalInfo) {
        message += `Note: ${deliveryDetails.additionalInfo}\n`;
      }
      message += '\n';
    }

    // Payment Options
    message += `*PAYMENT OPTIONS*\n\n`;

    // Option 1: Paystack Terminal transfer, with legacy DVA fallback
    if (virtualAccount && virtualAccount.accountNumber) {
      message += `*Option 1 - ${virtualAccount.isLegacyDva ? 'Legacy Bank Transfer' : 'Paystack Terminal Transfer'}:*\n`;
      message += `Bank: ${virtualAccount.bankName}\n`;
      message += `Account: ${virtualAccount.accountNumber}\n`;
      message += `Name: ${virtualAccount.accountName}\n\n`;
    }

    // Option 2: Direct Checkout Link with cart items
    const baseCheckoutUrl = generateCartCheckoutUrl({
      store: storeSettings,
      platformDomain: getPlatformDomainName()
    });

    if (baseCheckoutUrl) {
      let checkoutUrl;

      // For single item, use the product= format that checkout supports
      if (localItems.length === 1) {
        const item = localItems[0];
        const productId = item.product._id || item.product.id;
        const qty = item.qty;
        checkoutUrl = qty > 1
          ? `${baseCheckoutUrl}?product=${productId}&qty=${qty}`
          : `${baseCheckoutUrl}?product=${productId}`;
      } else {
        // For multiple items, use cart= format (checkout needs to support this)
        const cartItemsStr = localItems.map(item => {
          const productId = item.product._id || item.product.id;
          return `${productId}:${item.qty}`;
        }).join(',');
        checkoutUrl = `${baseCheckoutUrl}?cart=${cartItemsStr}`;
      }

      message += `*${virtualAccount?.accountNumber ? 'Option 2 - ' : ''}Pay Online:*\n`;
      message += `${checkoutUrl}\n\n`;
    }

    // Order Management Link (for both merchant and customer to view/edit order)
    if (orderUrl) {
      message += `*ORDER MANAGEMENT*\n`;
      message += `View/Edit Order: ${orderUrl}\n\n`;
    }

    // Footer
    message += `_Please confirm availability and total._`;

    return message;
  };

  // State for order submission
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle order submission
  const handleMakeOrder = async () => {
    // Validate required fields
    if (!customerDetails.fullName.trim()) {
      alert('Please enter your full name');
      return;
    }
    if (!customerDetails.whatsappNumber.trim()) {
      alert('Please enter your WhatsApp number');
      return;
    }

    const phoneNumber = formatPhoneNumber(storeSettings?.whatsappNumber);
    if (!phoneNumber) {
      alert('Store WhatsApp number not configured');
      return;
    }

    setIsSubmitting(true);

    try {
      // Create the order via API first
      const orderData = {
        storeSlug,
        customerName: customerDetails.fullName,
        customerEmail: customerDetails.email || null,
        customerPhone: customerDetails.whatsappNumber,
        items: localItems.map(item => ({
          productId: item.product._id || item.product.id,
          productName: item.product.name,
          quantity: item.qty,
          price: item.price ?? item.product.price,
          variationText: item.product.variationText || null
        })),
        subtotal,
        discountAmount,
        discountCode: appliedDiscount?.code || null,
        taxAmount,
        serviceCharge,
        shippingCost,
        grandTotal,
        deliveryAddress: deliveryDetails.address || null,
        deliveryCity: deliveryDetails.city || null,
        deliveryState: deliveryDetails.state || null,
        deliveryNotes: deliveryDetails.additionalInfo || null,
        // Chowdeck delivery method (if selected)
        deliveryMethod: deliveryMethod,
        chowdeckFeeId: deliveryMethod === 'CHOWDECK' ? chowdeckFeeId : null
      };

      const response = await axios.post('/whatsapp-orders/create', orderData);

      if (response.data.success) {
        // Use the customer URL (NO token) in the WhatsApp message
        // Merchant privileges are granted via session/cookie when logged in
        // This prevents customers from seeing/clicking a merchant token URL
        const orderUrl = response.data.order.orderUrl;

        // Generate message with order URL (no merchant token)
        const message = generateWhatsAppMessage(orderUrl);
        const encodedMessage = encodeURIComponent(message);
        const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;

        openWhatsAppOrder(whatsappUrl);
        onClose();
      } else {
        alert('Failed to create order. Please try again.');
      }
    } catch (error) {
      console.error('Error creating WhatsApp order:', error);
      alert('Failed to create order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '16px',
      backdropFilter: 'blur(4px)'
    }}>
      <style>
        {`
          @keyframes slideUp {
            from { transform: translateY(100%); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          .whatsapp-modal-content {
            animation: slideUp 0.3s ease-out;
          }

          .whatsapp-modal-content::-webkit-scrollbar {
            width: 6px;
          }

          .whatsapp-modal-content::-webkit-scrollbar-track {
            background: transparent;
          }

          .whatsapp-modal-content::-webkit-scrollbar-thumb {
            background: #d1d5db;
            border-radius: 3px;
          }

          @media (max-width: 640px) {
            .whatsapp-modal-content {
              max-height: 95vh !important;
              border-radius: 16px 16px 0 0 !important;
              position: fixed !important;
              bottom: 0 !important;
              left: 0 !important;
              right: 0 !important;
              margin: 0 !important;
              max-width: 100% !important;
            }
          }
        `}
      </style>

      <div
        className="whatsapp-modal-content"
        style={{
          backgroundColor: 'white',
          borderRadius: '24px',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #f3f4f6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 10,
          borderRadius: '24px 24px 0 0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111827' }}>
                Order via WhatsApp
              </h2>
              <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
                Complete your order details
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              border: 'none',
              background: '#f3f4f6',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px' }}>
          {/* Cart Items */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#6b7280',
              margin: '0 0 12px 0',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Your Items ({localItems.length})
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {localItems.map((item) => (
                <div
                  key={item.product._id || item.product.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '12px'
                  }}
                >
                  {/* Product Image */}
                  <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '8px',
                    backgroundColor: '#e5e7eb',
                    overflow: 'hidden',
                    flexShrink: 0
                  }}>
                    {item.product.imageUrl ? (
                      <img
                        src={item.product.imageUrl}
                        alt={item.product.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px'
                      }}>
                        📦
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      margin: 0,
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#111827',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {item.product.name}
                    </p>
                    <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#6b7280' }}>
                      {currency.format(item.price ?? item.product.price)}
                    </p>
                  </div>

                  {/* Quantity Controls */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      onClick={() => handleQuantityChange(item.product._id || item.product.id, item.qty - 1)}
                      disabled={item.qty <= 1}
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '6px',
                        border: 'none',
                        background: item.qty <= 1 ? '#e5e7eb' : '#fff',
                        cursor: item.qty <= 1 ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        fontWeight: '600',
                        color: item.qty <= 1 ? '#9ca3af' : '#374151',
                        boxShadow: item.qty > 1 ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                      }}
                    >
                      -
                    </button>
                    <span style={{
                      minWidth: '24px',
                      textAlign: 'center',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}>
                      {item.qty}
                    </span>
                    <button
                      onClick={() => handleQuantityChange(item.product._id || item.product.id, item.qty + 1)}
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '6px',
                        border: 'none',
                        background: '#fff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#374151',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                      }}
                    >
                      +
                    </button>
                  </div>

                  {/* Remove Button */}
                  <button
                    onClick={() => handleRemoveItem(item.product._id || item.product.id)}
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '6px',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                      <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Coupon Section */}
          <div style={{
            marginBottom: '20px',
            padding: '16px',
            backgroundColor: '#f9fafb',
            borderRadius: '12px'
          }}>
            {appliedDiscount ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 12px',
                backgroundColor: '#d1fae5',
                borderRadius: '8px',
                border: '1px solid #059669'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#065f46' }}>
                    {appliedDiscount.code} applied!
                  </span>
                </div>
                <button
                  onClick={handleRemoveCoupon}
                  style={{
                    padding: '4px 10px',
                    backgroundColor: 'white',
                    border: '1px solid #059669',
                    borderRadius: '6px',
                    color: '#059669',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Remove
                </button>
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Enter coupon code"
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      border: `1px solid ${couponError ? '#dc2626' : '#d1d5db'}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                      textTransform: 'uppercase'
                    }}
                  />
                  <button
                    onClick={handleApplyCoupon}
                    disabled={couponLoading || !couponCode.trim()}
                    style={{
                      padding: '10px 16px',
                      backgroundColor: couponLoading || !couponCode.trim() ? '#9ca3af' : '#111827',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: couponLoading || !couponCode.trim() ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {couponLoading ? 'Applying...' : 'Apply'}
                  </button>
                </div>
                {couponError && (
                  <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#dc2626' }}>
                    {couponError}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Customer Details Section */}
          <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <button
              onClick={() => setCustomerExpanded(!customerExpanded)}
              style={{
                width: '85%',
                padding: '16px 20px',
                backgroundColor: customerExpanded ? '#f0fdf4' : 'white',
                border: customerExpanded ? '2px solid #22c55e' : '1px solid #e5e7eb',
                borderRadius: customerExpanded ? '16px 16px 0 0' : '16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxShadow: customerExpanded ? '0 4px 12px rgba(34, 197, 94, 0.15)' : '0 2px 8px rgba(0,0,0,0.04)',
                transition: 'all 0.25s ease'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  background: customerExpanded ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' : '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={customerExpanded ? 'white' : '#6b7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
                <span style={{ fontSize: '15px', fontWeight: '600', color: '#111827' }}>
                  Your Details
                </span>
                {customerDetails.fullName && (
                  <span style={{
                    fontSize: '12px',
                    color: '#22c55e',
                    backgroundColor: '#dcfce7',
                    padding: '2px 8px',
                    borderRadius: '10px'
                  }}>
                    Filled
                  </span>
                )}
              </div>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6b7280"
                strokeWidth="2"
                style={{ transform: customerExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>

            {customerExpanded && (
              <div style={{
                width: '85%',
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '0 0 16px 16px',
                boxSizing: 'border-box'
              }}>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={customerDetails.fullName}
                    onChange={(e) => setCustomerDetails(prev => ({ ...prev, fullName: e.target.value }))}
                    placeholder="Enter your full name"
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: '1px solid #d1d5db',
                      borderRadius: '10px',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={customerDetails.email}
                    onChange={(e) => setCustomerDetails(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="your@email.com"
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: '1px solid #d1d5db',
                      borderRadius: '10px',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                    WhatsApp Number *
                  </label>
                  <input
                    type="tel"
                    value={customerDetails.whatsappNumber}
                    onChange={(e) => setCustomerDetails(prev => ({ ...prev, whatsappNumber: e.target.value }))}
                    placeholder="08012345678"
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: '1px solid #d1d5db',
                      borderRadius: '10px',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Delivery Details Section */}
          <div style={{ marginBottom: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <button
              onClick={() => setDeliveryExpanded(!deliveryExpanded)}
              style={{
                width: '85%',
                padding: '16px 20px',
                backgroundColor: deliveryExpanded ? '#f0fdf4' : 'white',
                border: deliveryExpanded ? '2px solid #22c55e' : '1px solid #e5e7eb',
                borderRadius: deliveryExpanded ? '16px 16px 0 0' : '16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxShadow: deliveryExpanded ? '0 4px 12px rgba(34, 197, 94, 0.15)' : '0 2px 8px rgba(0,0,0,0.04)',
                transition: 'all 0.25s ease'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  background: deliveryExpanded ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' : '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={deliveryExpanded ? 'white' : '#6b7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                </div>
                <span style={{ fontSize: '15px', fontWeight: '600', color: '#111827' }}>
                  Delivery Details
                </span>
                {deliveryDetails.state && (
                  <span style={{
                    fontSize: '12px',
                    color: '#22c55e',
                    backgroundColor: '#dcfce7',
                    padding: '2px 8px',
                    borderRadius: '10px'
                  }}>
                    {deliveryDetails.state}
                  </span>
                )}
              </div>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6b7280"
                strokeWidth="2"
                style={{ transform: deliveryExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>

            {deliveryExpanded && (
              <div style={{
                width: '85%',
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '0 0 16px 16px',
                boxSizing: 'border-box'
              }}>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                    State *
                  </label>
                  {merchantStates.length > 0 ? (
                    <select
                      value={deliveryDetails.state}
                      onChange={(e) => setDeliveryDetails(prev => ({ ...prev, state: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        border: '1px solid #d1d5db',
                        borderRadius: '10px',
                        fontSize: '14px',
                        outline: 'none',
                        backgroundColor: 'white',
                        boxSizing: 'border-box'
                      }}
                    >
                      <option value="">Select delivery location</option>
                      {merchantStates.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  ) : (
                    <p style={{
                      margin: 0,
                      padding: '10px 12px',
                      backgroundColor: '#fef3c7',
                      color: '#92400e',
                      border: '1px solid #fde68a',
                      borderRadius: '8px',
                      fontSize: '13px',
                      lineHeight: 1.4,
                    }}>
                      This merchant hasn&apos;t set up delivery locations yet. Please message them to arrange delivery manually.
                    </p>
                  )}
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                    City
                  </label>
                  <input
                    type="text"
                    value={deliveryDetails.city}
                    onChange={(e) => setDeliveryDetails(prev => ({ ...prev, city: e.target.value }))}
                    placeholder="Enter city"
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: '1px solid #d1d5db',
                      borderRadius: '10px',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                    Delivery Address
                  </label>
                  <input
                    type="text"
                    value={deliveryDetails.address}
                    onChange={(e) => setDeliveryDetails(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="Street address"
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: '1px solid #d1d5db',
                      borderRadius: '10px',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                    Additional Notes
                  </label>
                  <textarea
                    value={deliveryDetails.additionalInfo}
                    onChange={(e) => setDeliveryDetails(prev => ({ ...prev, additionalInfo: e.target.value }))}
                    placeholder="Landmark, special instructions..."
                    rows={2}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: '1px solid #d1d5db',
                      borderRadius: '10px',
                      fontSize: '14px',
                      outline: 'none',
                      resize: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* Shipping Info */}
                {shippingLoading && (
                  <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    backgroundColor: '#f0f9ff',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #3b82f6',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    <span style={{ fontSize: '13px', color: '#3b82f6' }}>Calculating shipping...</span>
                  </div>
                )}

                {shippingInfo && !shippingLoading && (
                  <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    backgroundColor: deliveryMethod === 'CHOWDECK' ? '#fff7ed' : '#f0fdf4',
                    borderRadius: '8px',
                    border: deliveryMethod === 'CHOWDECK' ? '1px solid #fed7aa' : '1px solid #bbf7d0'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '13px', color: deliveryMethod === 'CHOWDECK' ? '#c2410c' : '#166534' }}>
                        {deliveryMethod === 'CHOWDECK' ? '⚡ Chowdeck Express to' : '🚚 Shipping to'} {deliveryDetails.state}
                      </span>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: deliveryMethod === 'CHOWDECK' ? '#ea580c' : '#166534' }}>
                        {currency.format(shippingCost || 0)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Chowdeck Delivery Option */}
                {deliveryDetails.state && shippingInfo && (
                  <DeliveryMethodSelector
                    storeSlug={storeSlug}
                    deliveryState={deliveryDetails.state}
                    deliveryAddress={deliveryDetails.address}
                    deliveryCity={deliveryDetails.city}
                    standardShippingCost={shippingInfo?.shippingCost || 0}
                    standardShippingMethod={shippingInfo?.methodName || 'Standard Delivery'}
                    standardEstimate={shippingInfo?.estimatedDays || '3-5 business days'}
                    currency={{ format: (amount) => currency.format(amount) }}
                    onDeliveryMethodChange={handleDeliveryMethodChange}
                    disabled={false}
                  />
                )}
              </div>
            )}
          </div>

          {/* Order Summary */}
          <div style={{
            padding: '16px',
            backgroundColor: '#111827',
            borderRadius: '16px',
            marginBottom: '20px'
          }}>
            <h3 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#9ca3af',
              margin: '0 0 12px 0',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Order Summary
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <CustomerFacingSubtotal
                subtotal={subtotal}
                processingFee={serviceChargeWhoPays === 'customer' ? serviceCharge : 0}
                formatCurrency={currency.format}
                dark
                compact
                rowStyle={{ padding: 0 }}
                labelStyle={{ fontSize: '14px', color: '#d1d5db', fontWeight: 400 }}
                valueStyle={{ fontSize: '14px', color: 'white', fontWeight: 400 }}
                subLabel={false}
              />

              {discountAmount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '14px', color: '#4ade80' }}>Discount ({appliedDiscount?.code})</span>
                  <span style={{ fontSize: '14px', color: '#4ade80' }}>-{currency.format(discountAmount)}</span>
                </div>
              )}

              {taxAmount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '14px', color: '#d1d5db' }}>
                    {quoteTax.name || 'VAT'} ({quoteTax.rate || 0}%{quoteTax.inclusive ? ' incl.' : ''})
                  </span>
                  <span style={{ fontSize: '14px', color: 'white' }}>{currency.format(taxAmount)}</span>
                </div>
              )}

              {shippingCost > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '14px', color: deliveryMethod === 'CHOWDECK' ? '#fb923c' : '#d1d5db' }}>
                    {deliveryMethod === 'CHOWDECK' ? '⚡ Chowdeck Express' : 'Shipping'}
                  </span>
                  <span style={{ fontSize: '14px', color: deliveryMethod === 'CHOWDECK' ? '#fb923c' : 'white' }}>
                    {currency.format(shippingCost)}
                  </span>
                </div>
              )}

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                paddingTop: '12px',
                marginTop: '8px',
                borderTop: '1px solid #374151'
              }}>
                <span style={{ fontSize: '16px', fontWeight: '700', color: 'white' }}>Total</span>
                <span style={{ fontSize: '18px', fontWeight: '700', color: '#22c55e' }}>
                  {calculationsLoading ? '...' : currency.format(grandTotal)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div style={{
          padding: '16px 24px 24px',
          borderTop: '1px solid #f3f4f6',
          display: 'flex',
          gap: '12px',
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'white',
          borderRadius: '0 0 24px 24px'
        }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '14px',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: 'none',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Back
          </button>
          <button
            onClick={handleMakeOrder}
            disabled={isSubmitting || calculationsLoading || localItems.length === 0 || !deliveryDetails.state || !deliveryDetails.address}
            style={{
              flex: 2,
              padding: '14px',
              background: (isSubmitting || calculationsLoading || localItems.length === 0 || !deliveryDetails.state || !deliveryDetails.address)
                ? '#9ca3af'
                : 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: (isSubmitting || calculationsLoading || localItems.length === 0 || !deliveryDetails.state || !deliveryDetails.address) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: (!isSubmitting && !calculationsLoading && localItems.length > 0 && deliveryDetails.state && deliveryDetails.address) ? '0 4px 14px rgba(37, 211, 102, 0.3)' : 'none'
            }}
          >
            {isSubmitting ? (
              <>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid white',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Creating Order...
              </>
            ) : calculationsLoading ? (
              <>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid white',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Calculating...
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Make Order
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppOrderModal;
